import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useState } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";

import {
  GridInputs,
  multiSelect,
  Table,
} from "../../../../../../../components/Functional";
import { Button, Modal } from "../../../../../../../components/ui";
import ThreeStateCheckbox from "../../../../../../../components/ui/Input/components/ThreeStateCheckbox";
import { convertToFilter } from "../../../../../../../util";

import { ADD_SHIPMENT_WORK as addShipmentWorkMutation } from "../../../../../../../graphql/mutation/Shipment";
import { FETCH_ARTISTS as fetchArtists } from "../../../../../../../graphql/query/Artist";
import { FETCH_INSTANCES_V2 as fetchInstancesV2 } from "../../../../../../../graphql/query/Instance";
import { useCompanies } from "../../../../../../../hooks/useCompanies";

const getErrorMessage = ({ payloadError, mutationError }) => {
  if (payloadError) return payloadError;

  const graphQLError = mutationError?.graphQLErrors?.[0]?.message;
  if (graphQLError) return graphQLError;

  return mutationError?.message || "";
};

/** Map API warningItems to UI entry shape. Uses structured data only; no string parsing. */
const getShipmentWorkWarningEntries = warningItems => {
  if (!Array.isArray(warningItems) || warningItems.length === 0) return [];
  return warningItems.map(item => ({
    instanceId: item?.instanceId ?? null,
    warehouseItemId: item?.warehouseItemId ?? null,
    previousShipmentId: item?.previousShipmentId ?? null,
    warehouseMoveId: item?.warehouseMoveId ?? null,
    categoryDescription: item?.categoryDescription ?? null,
    alreadyInShipment: Boolean(item?.alreadyInShipment),
    message: item?.message ?? "",
  }));
};

const FilterBar = props => {
  const { formik } = props || {};
  const { data: { artists = [] } = {} } = useQuery(fetchArtists());
  const { companies } = useCompanies();

  const inputs = {
    className: "grid-cols-5 gap-4 my-6",
    inputs: [
      {
        label: "Company",
        name: "companyId",
        type: "multi-select",
        options: companies?.edges?.map(item => ({
          label: item?.name,
          value: item?.id,
        })),
      },
      { label: "Control Reference", name: "ref", type: "text" },
      {
        label: "Artist",
        name: "_edition._artwork._artist.id",
        type: "multi-select",
        options: artists?.map(item => ({ label: item?.name, value: item?.id })),
      },
      { label: "Title", name: "_edition._artwork.title", type: "text" },
      { label: "Edition #", name: "editionNumber", type: "number" },
      { label: "Name", name: "name", type: "text" },
      { label: "Artist Ref", name: "artistRef", type: "text" },
      { label: "Series", name: "_edition._artwork.series", type: "text" },
      {
        designType: "threeStateCheckbox",
        label: "Photographed",
        name: "_edition._artwork.photographed",
        type: "toggle",
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  return <GridInputs {...inputProps} />;
};

const AddWorkModal = props => {
  const { closeModal, shipment } = props;
  const [TaWarning, setTaWarning] = useState(false);
  const [workAddWarnings, setWorkAddWarnings] = useState([]);
  const [workAddAddedCount, setWorkAddAddedCount] = useState(0);

  const [
    addShipmentWork,
    {
      reset: resetAdd,
      loading,
      error: mutationError,
      data: mutationData,
    },
  ] = useMutation(addShipmentWorkMutation(), {
    errorPolicy: "all",
    refetchQueries: ["FetchWarehouseMoveV2"],
  });
  // Only show error if mutation actually failed (not successful)
  const isSuccess = mutationData?.addShipmentWork?.success === true;
  const addShipmentWorkError =
    !isSuccess && mutationData?.addShipmentWork?.error
      ? mutationData.addShipmentWork.error
      : null;
  const addShipmentWorkWarningEntries = workAddWarnings.length
    ? workAddWarnings
    : getShipmentWorkWarningEntries(mutationData?.addShipmentWork?.warningItems ?? []);
  const addShipmentWorkWarnings = addShipmentWorkWarningEntries;
  const addShipmentWorkErrorMessage = getErrorMessage({
    payloadError: addShipmentWorkError,
    mutationError,
  });

  const handleClick = async () => {
    refetch();

    try {
      const { data } = await addShipmentWork({
        variables: {
          input: {
            id: shipment?.id,
            instanceIds: selectedInstances,
          },
        },
      });
      let hasTAWork = [];
      if (edges?.length > 0) {
        // Filter search results to only get the instances that were actually selected for shipment
        const selectedInstancesData = edges.filter(edge =>
          selectedInstances.includes(edge.id),
        );

        // Check if any of the selected instances have TA (Temporary Admission) bond type
        // This will only trigger the warning if works being added are actually on TA
        hasTAWork = selectedInstancesData.filter(
          edge => edge?.bondType?.type === "TA",
        );
        if (hasTAWork && hasTAWork.length > 0) {
          setTaWarning(true);
        }
      }

      if (data?.addShipmentWork?.success) {
        const warningEntries = getShipmentWorkWarningEntries(
          data?.addShipmentWork?.warningItems ?? [],
        );
        if (hasTAWork.length < 1 && !warningEntries.length) {
          closeModal?.();
        }
        if (warningEntries.length) {
          setWorkAddWarnings(warningEntries);
          setWorkAddAddedCount(data?.addShipmentWork?.addedCount ?? 0);
          // Clear selections/filters, but keep warnings visible.
          filterFormik?.resetForm();
          formik?.resetForm();
          resetAdd?.();
        } else {
          resetAll();
        }
      }
    } catch (error) {
      // Keep errors local to this modal and avoid bubbling to a global ErrorBoundary.
      console.warn("addShipmentWork failed", error);
    }
  };

  const formik = useFormik({
    initialValues: {},
    onSubmit: handleClick,
  });

  const filterFormik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({
      editionNumber: Yup.number()
        .nullable()
        .integer()
        .positive()
        .typeError("Invalid input"),
    }),
  });

  const selectedInstances = Object.keys(
    formik?.values?.selectedInstances?.ids || {},
  )?.map(item => parseInt(item.replace("_", "")));

  const filters = convertToFilter({
    values: filterFormik?.values,
    skipKeys: ["limit", "offset"],
  });

  const { data: { instancesV2: { edges = [] } = {} } = {}, refetch } = useQuery(
    fetchInstancesV2(),
    {
      enabled: false,
      variables: {
        input: { ...filters },
      },
    },
  );

  const EditionNumber = ({ values }) => {
    if (values?.edition?.number === 1 || !values?.editionNumber) return null;
    return (
      <div className="flex flex-col">
        <span className="text-base font-medium leading-5 text-black">{`${values?.editionNumber}/${values?.edition?.number}`}</span>
      </div>
    );
  };

  const Location = ({ values }) => {
    const description =
      values?.warehouseItem?.[0]?.itemSite?.location?.description || "—";

    return (
      <div className="flex flex-col">
        <span className="text-base font-medium leading-5 text-black">
          {description}
        </span>
      </div>
    );
  };

  const multiSelectProps = {
    formik,
    label: "",
    name: "selectedInstances",
    keyName: "id",
    showAll: false,
  };

  const tableProps = {
    dataKey: "instancesV2",
    FETCH_QUERY: fetchInstancesV2({
      // Instance scalar fields
      name: true,
      editionNumber: true,
      // Instance associations
      edition: {
        ap: true,
        number: true,
        artwork: {
          title: true,
          artists: {
            name: true,
          },
        },
      },
      status: {
        name: true,
      },
      warehouseItem: {
        itemSite: {
          location: {
            description: true,
          },
        },
      },
    }),
    headers: [
      multiSelect(multiSelectProps),
      {
        label: "Status",
        name: "status.name",
        type: "label",
      },
      { label: "Reference", name: "name", type: "label" },
      {
        label: "Artist",
        name: "edition.artwork.artists",
        type: "custom",
        component: props => (
          <div className="flex flex-col">
            <span className="text-base font-medium leading-5 text-black">
              {props.values?.edition?.artwork?.artists
                ?.map(artist => artist.name)
                .join(", ")}
            </span>
          </div>
        ),
      },
      {
        label: "Title",
        name: "edition.artwork.title",
        type: "label",
        widthInPx: 400,
      },
      {
        label: "Edition",
        name: "editionNumber",
        type: "custom",
        component: EditionNumber,
      },
      {
        label: "AP",
        name: "edition.ap",
        type: "custom",
        component: props => (
          <ThreeStateCheckbox
            checked={props.values?.edition?.ap}
            disabled={true}
          />
        ),
      },
      { label: "Year", name: "edition.year", type: "label" },
      { label: "Medium", name: "edition.artwork.medium", type: "label" },
      {
        label: "Dimensions",
        name: "edition.artwork.dimensions",
        type: "label",
      },
      {
        label: "Fabricated",
        name: "fabricated",
        type: "custom",
        component: props => (
          <ThreeStateCheckbox
            checked={props.values?.fabricated}
            disabled={true}
          />
        ),
      },
      {
        label: "Location",
        name: "location",
        component: Location,
        type: "custom",
      },
    ],
    inputs: [],
    limit: 10,
    skip: () => !filterFormik?.isValid,
    variables: convertToFilter({
      values: filterFormik?.values,
      skipKeys: ["limit", "offset"],
    }),
    pagination: {
      variant: "sm",
    },
    variant: "dynamicHeight",
  };

  const resetAll = () => {
    filterFormik?.resetForm();
    formik?.resetForm();
    resetAdd?.();
    setWorkAddWarnings([]);
    setWorkAddAddedCount(0);
  };

  if (addShipmentWorkErrorMessage) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col">
          <div className="text-2xl font-bold">Error Adding Work</div>
          <div className="mt-6">{addShipmentWorkErrorMessage}</div>
        </div>
        <div className="mt-8 flex flex-row">
          <div className="flex flex-col">
            <div>
              <Button
                className="mr-4"
                label="Close"
                onClick={() => {
                  resetAll();
                  closeModal?.();
                  setTaWarning(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!addShipmentWorkErrorMessage && addShipmentWorkWarnings.length) {
    const warningEntries = addShipmentWorkWarnings;
    const addedCount =
      workAddWarnings.length > 0
        ? workAddAddedCount
        : (mutationData?.addShipmentWork?.addedCount ?? 0);
    const groupedByInstance = warningEntries.reduce((acc, entry) => {
      const key = entry?.instanceId ? String(entry.instanceId) : "unknown";
      const existing = acc[key] || { instanceId: entry?.instanceId, entries: [] };
      return { ...acc, [key]: { ...existing, entries: [...existing.entries, entry] } };
    }, {});
    const groups = Object.values(groupedByInstance);

    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col">
          <div className="text-2xl font-bold">
            {addedCount > 0 ? "Work added with warnings" : "No work added"}
          </div>
          <div className="mt-6">
            Some warehouse items could not be added or were already in your
            shipment:
          </div>
          <div className="mt-4 rounded bg-[#2222220D] p-3 text-sm text-black">
            <div className="flex flex-col gap-4">
              {groups.map(group => {
                const instance = edges?.find(e => e?.id === group?.instanceId);
                const workTitle = instance?.edition?.artwork?.title;
                const workReference = instance?.name;
                const workMeta = [workReference, workTitle]
                  .filter(Boolean)
                  .join(" — ");

                return (
                  <div
                    key={group?.instanceId ? `instance-${group.instanceId}` : "instance-unknown"}
                    className="flex flex-col gap-2"
                  >
                    <div className="font-medium">
                      {group?.instanceId ? (
                        <>
                          Adding Instance:{" "}
                          <Link
                            className="underline"
                            to={`/instance/${group.instanceId}/details`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            #{group.instanceId}
                          </Link>
                        </>
                      ) : (
                        "Instance"
                      )}
                      {workMeta ? ` — ${workMeta}` : ""}
                    </div>
                    <ul className="flex list-disc flex-col gap-1 pl-5">
                      {group.entries.map((entry, index) => (
                        <li key={`${entry?.instanceId ?? "i"}-${entry?.warehouseItemId ?? "w"}-${index}`}>
                          {entry.warehouseItemId ? (
                            <>
                              Item:{" "}
                              <Link
                                className="underline"
                                to={`/warehouse/${entry.warehouseItemId}/movements`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                #{entry.warehouseItemId}
                              </Link>
                            </>
                          ) : (
                            <span>Warehouse item</span>
                          )}
                          {entry.categoryDescription ? (
                            <span className="ml-1 text-black/70">
                              ({entry.categoryDescription})
                            </span>
                          ) : null}
                          {entry.previousShipmentId ? (
                            <>
                              {" "}
                              (
                              <Link
                                className="underline"
                                to={`/shipments/${entry.previousShipmentId}/items`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Shipment #{entry.previousShipmentId}
                              </Link>
                              )
                            </>
                          ) : null}
                          {entry.alreadyInShipment
                            ? entry?.message ?? " — Already in your shipment"
                            : entry.message
                              ? ` — ${entry.message}`
                              : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-row">
          <div className="flex flex-col">
            <div>
              <Button
                className="mr-4"
                label="Close"
                onClick={() => {
                  resetAll();
                  closeModal?.();
                  setTaWarning(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  } else if (!addShipmentWorkErrorMessage && TaWarning) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col">
          <div className="text-2xl font-bold">Instance on TA</div>
          <div className="mt-6">
            This instance is on TA. Please comply with TA shipping restrictions.
          </div>
        </div>
        <div className="mt-8 flex flex-row">
          <div className="flex flex-col">
            <div>
              <Button
                className="mr-4"
                label="Close"
                onClick={() => {
                  resetAll();
                  closeModal?.();
                  setTaWarning(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex w-full flex-col">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Add Work: Select an Instance</div>
        </div>
        <div className="flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-4"
              label="Cancel"
              onClick={() => {
                resetAll();
                closeModal?.();
              }}
            />
            <Button
              label={loading ? "Adding" : "Add"}
              disabled={loading || !selectedInstances?.length}
              onClick={formik?.submitForm}
            />
          </div>
        </div>
      </div>
      <FilterBar formik={filterFormik} />
      <p className="mt-4">
        {selectedInstances?.length} Selected{" "}
        <span className="ml-8 text-red-500">{addShipmentWorkErrorMessage}</span>
      </p>
      <Table {...tableProps} />
    </div>
  );
};

const AddWork = props => {
  const { shipment } = props || {};

  const modalProps = {
    body: AddWorkModal,
    closeOnBackdrop: false,
    shipment,
    hideCloseButton: true,
    scale: "md",
  };

  return (
    <Modal {...modalProps}>
      <Button label="Add Work" action="black" />
    </Modal>
  );
};

export default AddWork;
